import React, { useState, useEffect } from 'react';
import { portfolioReportsAPI } from '../../services/api';

/**
 * Sidebar portfolio selector for the Portfolio Reports app.
 *
 * For Drummond users, portfolios are grouped by brand:
 *   - Drummond portfolios loaded upfront
 *   - Brand groups lazy-loaded when expanded
 *
 * For public (non-Drummond) users the backend returns their domain's
 * portfolios as a flat list.
 *
 * Unlike the Factsheets variant, this selector does NOT auto-select the
 * first portfolio — Portfolio Reports intentionally starts with no
 * selection so the user must pick a quarter + portfolio explicitly.
 */
const PortfolioSelector = ({ selectedPortfolio, selectedDomain, onSelectPortfolio, isDrummondUser, quarter, brandCopies = [] }) => {
  const [portfolios, setPortfolios] = useState([]);
  const [brands, setBrands] = useState(null); // [{domain, company_name, portfolio_count}]
  const [brandPortfolios, setBrandPortfolios] = useState({}); // domain -> portfolio[]
  const [loadingBrands, setLoadingBrands] = useState({}); // domain -> bool
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [collapsedGroups, setCollapsedGroups] = useState({});

  useEffect(() => {
    loadPortfolios();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDrummondUser, quarter]);

  const loadPortfolios = async () => {
    // Client users need a quarter to filter by publish status
    if (!isDrummondUser && !quarter) {
      setPortfolios([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      if (isDrummondUser) {
        // Load Drummond portfolios + lightweight brand list in parallel
        const [portfolioRes, brandRes] = await Promise.all([
          portfolioReportsAPI.getPortfolios(),
          portfolioReportsAPI.getBrands().catch(() => null),
        ]);

        setPortfolios(portfolioRes.portfolios || []);
        if (brandRes) {
          setBrands(brandRes.brands || []);
        }
      } else {
        // Client users get flat filtered list scoped to their domain + published for quarter
        const res = await portfolioReportsAPI.getPortfolios(quarter);
        setPortfolios(res.portfolios || []);
      }
    } catch (err) {
      console.error('Error loading portfolios:', err);
      setError('Failed to load portfolios');
    } finally {
      setIsLoading(false);
    }
  };

  const toggleGroup = async (groupKey) => {
    const isCurrentlyCollapsed = collapsedGroups[groupKey];
    const willExpand = isCurrentlyCollapsed !== false; // default state is collapsed for brands

    setCollapsedGroups(prev => ({
      ...prev,
      [groupKey]: !willExpand,
    }));

    // Lazy-load brand portfolios on first expand
    if (willExpand && groupKey !== 'drummond' && !brandPortfolios[groupKey]) {
      setLoadingBrands(prev => ({ ...prev, [groupKey]: true }));
      try {
        const res = await portfolioReportsAPI.getBrandPortfolios(groupKey);
        setBrandPortfolios(prev => ({
          ...prev,
          [groupKey]: res.portfolios || [],
        }));
      } catch (err) {
        console.error(`Error loading portfolios for ${groupKey}:`, err);
        setBrandPortfolios(prev => ({
          ...prev,
          [groupKey]: [], // empty so it doesn't retry infinitely
        }));
      } finally {
        setLoadingBrands(prev => ({ ...prev, [groupKey]: false }));
      }
    }
  };

  const renderPortfolioItem = (portfolio, domain = null) => {
    const isActive =
      selectedPortfolio?.portfolio_code === portfolio.portfolio_code &&
      (selectedDomain || null) === (domain || null);
    const key = domain ? `${portfolio.portfolio_code}__${domain}` : portfolio.portfolio_code;
    return (
      <button
        key={key}
        type="button"
        className={`portfolio-item ${isActive ? 'active' : ''}`}
        onClick={() => onSelectPortfolio(portfolio, domain)}
        title={portfolio.portfolio_code}
      >
        <span className="portfolio-name">
          {portfolio.name}
          {domain && brandCopies.some((c) => c.portfolio_code === portfolio.portfolio_code && c.domain === domain) && (
            <span className="own-copy-tag" title="This brand has its own copy of this report: Drummond edits don't reach it">
              Own copy
            </span>
          )}
        </span>
        <span className="portfolio-code">{portfolio.portfolio_code}</span>
      </button>
    );
  };

  if (isLoading) {
    return (
      <div className="portfolio-list">
        <div className="sidebar-loading">Loading…</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="portfolio-list">
        <div className="sidebar-error">
          <i className="bi bi-exclamation-triangle me-1"></i>
          {error}
        </div>
      </div>
    );
  }

  // Drummond user with brand groups
  if (isDrummondUser && brands && brands.length > 0) {
    return (
      <div className="portfolio-list">
        {/* Drummond portfolios — loaded upfront, expanded by default */}
        <div className="portfolio-group">
          <div
            className="portfolio-group-header"
            onClick={() => toggleGroup('drummond')}
          >
            <i className={`bi bi-chevron-${collapsedGroups['drummond'] === true ? 'right' : 'down'} me-1`}></i>
            DRUMMOND PORTFOLIOS
            <span className="portfolio-count">
              {portfolios.filter(p => p.has_portfolio_data !== false).length}
            </span>
          </div>
          {collapsedGroups['drummond'] !== true && (
            <div className="portfolio-group-items">
              {portfolios
                .filter(p => p.has_portfolio_data !== false)
                .map(p => renderPortfolioItem(p, null))}
            </div>
          )}
        </div>

        {/* Brand groups — lazy-loaded on expand, collapsed by default */}
        {brands.map(brand => {
          const domain = brand.domain;
          const isExpanded = collapsedGroups[domain] === false;
          const domainPortfolios = brandPortfolios[domain];
          const isLoadingDomain = loadingBrands[domain];

          return (
            <div key={domain} className="portfolio-group">
              <div
                className="portfolio-group-header"
                onClick={() => toggleGroup(domain)}
              >
                <i className={`bi bi-chevron-${isExpanded ? 'down' : 'right'} me-1`}></i>
                {brand.company_name.toUpperCase()}
                <span className="portfolio-count">{brand.portfolio_count}</span>
              </div>
              {isExpanded && (
                <div className="portfolio-group-items">
                  {isLoadingDomain && (
                    <div className="sidebar-loading">Loading…</div>
                  )}
                  {domainPortfolios &&
                    domainPortfolios
                      .filter(p => p.has_portfolio_data !== false)
                      .map(p => renderPortfolioItem(p, domain))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  }

  // Client users or Drummond without brand data — flat list
  return (
    <div className="portfolio-list">
      <h4 className="list-heading">Portfolios</h4>
      {portfolios
        .filter(p => p.has_portfolio_data !== false)
        .map(p => renderPortfolioItem(p, null))}
    </div>
  );
};

export default PortfolioSelector;
